import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import api from '../services/api';
import './MalwareTool.css';

const MalwareTool = () => {
  const { user, isInJail, getJailTimeRemaining } = useAuth();
  const { addNotification } = useNotifications();
  const [emailAddress, setEmailAddress] = useState('');
  const [activeMalwareAttacks, setActiveMalwareAttacks] = useState([]);
  const [malwarePartsCount, setMalwarePartsCount] = useState(0);
  const [discoveredEmails, setDiscoveredEmails] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stage3Status, setStage3Status] = useState(null);
  const [stage3Loading, setStage3Loading] = useState(false);
  const [previousEmailCount, setPreviousEmailCount] = useState(0);
  const [newEmailNotification, setNewEmailNotification] = useState(null);
  const [cancellingHack, setCancellingHack] = useState(false);

  useEffect(() => {
    fetchMalwareStatus();
    // Auto-refresh every 10 seconds for real-time updates
    const interval = setInterval(fetchMalwareStatus, 10000);
    return () => clearInterval(interval);
  }, [user]);

  const fetchMalwareStatus = async () => {
    try {
      if (!user?.token) return;

      const response = await api.get('/malware/status');

      if (response.status === 200) {
        const data = response.data;
        if (data.success) {
          const newEmails = data.data.discoveredEmails || [];
          
          // Check if new emails were discovered
          if (!initialLoading && newEmails.length > previousEmailCount && newEmails.length > 0) {
            const newestEmail = newEmails[newEmails.length - 1];
            setNewEmailNotification({
              email: newestEmail,
              timestamp: new Date()
            });
            
            // Clear notification after 10 seconds
            setTimeout(() => {
              setNewEmailNotification(null);
            }, 10000);
          }
          
          setActiveMalwareAttacks(data.data.activeMalwareAttacks || []);
          setMalwarePartsCount(data.data.malwarePartsCount || 0);
          setDiscoveredEmails(newEmails);
          setPreviousEmailCount(newEmails.length);
        }
      }
    } catch (err) {
      console.error('Error fetching malware status:', err);
    } finally {
      setInitialLoading(false);
    }
  };

  const executeMalware = async () => {
    if (!emailAddress.trim()) {
      setError('Please enter or select an email address');
      return;
    }

    setActionLoading(true);
    setError(null);

    try {
      const response = await api.post('/malware/execute', { emailAddress: emailAddress.trim() });

      const data = response.data;

      if (response.status === 200 && data.success) {
        alert(`🚀 Malware Attack Initiated!\n\nTarget: ${emailAddress}\nEstimated completion: ${new Date(data.data.estimatedCompletion).toLocaleString()}\n\nYour malware is now infiltrating the target system...`);
        setEmailAddress('');
        fetchMalwareStatus();
      } else {
        setError(data.message || 'Failed to execute malware attack');
      }
    } catch (err) {
      console.error('Error executing malware:', err);
      setError('Network error. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const checkMalwareResults = async (attackId) => {
    try {
      const response = await api.get(`/malware/check/${attackId}`);

      if (response.status === 200) {
        const data = response.data;
        if (data.success && data.data.completed) {
          if (data.data.success && !data.data.swatDetected) {
            // Show success message with Stage 3 option
            const continueToStage3 = window.confirm(
              `🎉 Stage 2 Complete - Malware Attack Successful!\n\n` +
              `Target: ${data.data.targetEmail}\n` +
              `Casino: ${data.data.casinoName}\n\n` +
              `✅ Employee system compromised!\n` +
              `🎯 Stage 3 Ready: Vault Looting at 3 WR coins/second\n\n` +
              `Would you like to start Stage 3 vault looting now?`
            );
            
            if (continueToStage3) {
              // Automatically trigger Stage 3 looting
              await startStage3Looting();
            }
            
          } else if (data.data.swatDetected) {
            alert(`🚨 SWAT DETECTED!\n\nTarget: ${data.data.targetEmail}\n\nYour malware attack was detected by law enforcement. Mission failed!`);
          } else {
            alert(`❌ Malware Attack Failed!\n\nTarget: ${data.data.targetEmail}\n\nThe target system's security was too strong. Try again with better equipment.`);
          }
          fetchMalwareStatus();
        }
      }
    } catch (err) {
      console.error('Error checking malware results:', err);
    }
  };

  const stopMalwareAttack = async (attackId) => {
    if (!window.confirm('Are you sure you want to stop this malware attack?\n\nThis will cancel the operation and you may need to wait before starting a new attack.')) {
      return;
    }

    try {
      const response = await api.post('/malware/stop', { attackId });

      if (response.status === 200) {
        const data = response.data;
        if (data.success) {
          alert('✅ Malware attack stopped successfully!');
          fetchMalwareStatus();
        }
      }
    } catch (err) {
      console.error('Error stopping malware attack:', err);
      alert('❌ Error stopping malware attack. Please try again.');
    }
  };

  const deleteDiscoveredEmail = async (emailToDelete) => {
    if (!window.confirm(`Are you sure you want to delete this discovered email?\n\n${emailToDelete}\n\nThis will remove the email from your discovered list permanently.`)) {
      return;
    }

    setActionLoading(true);
    setError(null);

    try {
      const response = await api.post('/malware/delete-email', { emailAddress: emailToDelete });

      const data = response.data;

      if (response.status === 200 && data.success) {
        alert(`✅ Email deleted successfully!\n\n${emailToDelete} has been removed from your discovered emails.`);
        // Clear the selected email if it was the one being deleted
        if (emailAddress === emailToDelete) {
          setEmailAddress('');
        }
        fetchMalwareStatus(); // Refresh the email list
      } else {
        setError(data.message || 'Failed to delete email');
      }
    } catch (err) {
      console.error('Error deleting email:', err);
      setError('Network error. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const startStage3Looting = async () => {
    setStage3Loading(true);
    setError(null);

    try {
      const response = await api.post('/malware/stage3/start', {});

      const data = response.data;

      if (response.status === 200 && data.success) {
        alert(`💰 Stage 3 Started!\n\n${data.message}\n\nYou are now earning 3 WR coins per second!`);
        setStage3Status({ active: true, startTime: new Date() });
        fetchMalwareStatus();
      } else {
        setError(data.message || 'Failed to start Stage 3 looting');
      }
    } catch (err) {
      console.error('Error starting Stage 3:', err);
      setError('Network error. Please try again.');
    } finally {
      setStage3Loading(false);
    }
  };

  const collectStage3Coins = async () => {
    setStage3Loading(true);

    try {
      const response = await api.post('/malware/stage3/collect', {});

      const data = response.data;

      if (response.status === 200 && data.success) {
        if (data.data.coinsEarned > 0) {
          // Show notification for successful coin collection
          addNotification({
            type: 'casino_hack_collection',
            category: 'hacking',
            title: '💰 Casino Vault Looted!',
            message: `Collected ${data.data.coinsEarned.toFixed(3)} WR Coins from casino vault`,
            details: `Total looted: ${data.data.totalLooted.toFixed(3)} WR Coins | New balance: ${data.data.newBalance.toFixed(3)} WR Coins`,
            icon: '💰',
            color: '#4CAF50'
          });
        }
        fetchMalwareStatus();
      } else {
        setError(data.message || 'Failed to collect coins');
      }
    } catch (err) {
      console.error('Error collecting coins:', err);
      setError('Network error. Please try again.');
    } finally {
      setStage3Loading(false);
    }
  };

  const cancelCasinoHack = async () => {
    if (!user?.casinoHack || !user.casinoHack.isActive) {
      alert('No active casino hack to cancel.');
      return;
    }

    const stageName = user.casinoHack.currentStage === 1 ? 'Stage 1 (Network Injection)' :
                     user.casinoHack.currentStage === 2 ? 'Stage 2 (Malware Attack)' :
                     user.casinoHack.currentStage === 3 ? 'Stage 3 (Vault Looting)' : 'Casino Hack';

    if (!window.confirm(`🚫 Cancel ${stageName}?\n\nCasino: ${user?.casinoHack?.casinoName || 'Unknown'}\n\nThis will stop the hack and refund your network injection tools. Any coins looted in Stage 3 will be kept.`)) {
      return;
    }

    setCancellingHack(true);
    setError(null);

    try {
      const response = await api.post('/treasure-hunt/casinos/vault/stop-attack', { casinoId: user.casinoHack.casinoId });

      const data = response.data;

      if (response.status === 200 && data.success) {
        alert(`✅ Casino hack cancelled!\n\n${data.message}`);
        fetchMalwareStatus(); // Refresh to update UI
      } else {
        setError(data.message || 'Failed to cancel casino hack');
      }
    } catch (err) {
      console.error('Error cancelling casino hack:', err);
      setError('Network error. Please try again.');
    } finally {
      setCancellingHack(false);
    }
  };

  const formatTimeRemaining = (estimatedCompletion) => {
    const now = new Date();
    const completion = new Date(estimatedCompletion);
    const timeRemaining = completion.getTime() - now.getTime();
    
    if (timeRemaining <= 0) {
      return 'Completed! Click to collect.';
    }
    
    const hours = Math.floor(timeRemaining / (60 * 60 * 1000));
    const minutes = Math.floor((timeRemaining % (60 * 60 * 1000)) / (60 * 1000));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  const getProgressPercentage = (startTime, estimatedCompletion) => {
    const start = new Date(startTime).getTime();
    const end = new Date(estimatedCompletion).getTime();
    const now = new Date().getTime();
    
    const totalTime = end - start;
    const elapsed = now - start;
    
    return Math.min(100, Math.max(0, (elapsed / totalTime) * 100));
  };

  // Only show loading on first visit
  if (initialLoading) {
    return (
      <div className="malware-tool">
        <div className="loading">Loading malware tools...</div>
      </div>
    );
  }

  return (
    <div className="malware-tool">
      <div className="malware-header">
        <h2>🦠 Malware Attack Tool</h2>
        <p>Execute sophisticated malware attacks using casino employee email addresses obtained from Stage 1 vault hacks.</p>
      </div>

      {/* Stats Overview */}
      <div className="malware-stats">
        <div className="stat-card">
          <h3>🔧 Equipment</h3>
          <div className="stat-value">{malwarePartsCount}</div>
          <div className="stat-label">Malware Parts</div>
        </div>
        <div className="stat-card">
          <h3>⚡ Active Attacks</h3>
          <div className="stat-value">{activeMalwareAttacks.length}</div>
          <div className="stat-label">Running Attacks</div>
        </div>
        <div className="stat-card">
          <h3>⏱️ Attack Time</h3>
          <div className="stat-value">12-16</div>
          <div className="stat-label">Hours (Random)</div>
        </div>
      </div>

      {/* New Email Discovery Notification */}
      {newEmailNotification && (
        <div className="new-email-notification" style={{
          background: 'linear-gradient(45deg, #4CAF50, #66BB6A)',
          color: 'white',
          padding: '20px',
          margin: '15px 0',
          borderRadius: '12px',
          border: '3px solid #81C784',
          boxShadow: '0 4px 20px rgba(76, 175, 80, 0.3)',
          animation: 'pulse 2s infinite'
        }}>
          <h3 style={{ margin: '0 0 10px 0', fontSize: '18px' }}>🎉 Stage 1 Complete - Employee Email Discovered!</h3>
          <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>
            📧 {newEmailNotification.email}
          </div>
          <p style={{ margin: '0', fontSize: '14px', opacity: '0.9' }}>
            ✅ Network injection stage complete! You can now use this employee email for Stage 2 malware attacks.
          </p>
        </div>
      )}

      {/* Jail Status Warning */}
      {isInJail() && (
        <div className="jail-warning" style={{
          background: '#ff4444',
          color: 'white',
          padding: '15px',
          margin: '10px 0',
          borderRadius: '8px',
          border: '2px solid #ff6666'
        }}>
          <h3>🔒 You are currently in jail!</h3>
          <p>Time remaining: {getJailTimeRemaining()} minutes</p>
          <p>You cannot start new malware attacks while incarcerated.</p>
        </div>
      )}

      {/* Malware Execution */}
      <div className="malware-execution">
        <h3>🎯 Execute Malware Attack (Stage 2)</h3>
        
        {/* Stage Progress Indicator */}
        <div className="stage-progress">
          <div className="stage-item" style={{ color: '#4CAF50' }}>
            <span className="stage-icon">✅</span>
            <span className="stage-text">Stage 1: Network Injection</span>
          </div>
          <div className="stage-arrow">→</div>
          <div className="stage-item" style={{
            color: activeMalwareAttacks.length > 0 ? '#FF9800' : discoveredEmails.length > 0 ? '#2196F3' : '#666'
          }}>
            <span className="stage-icon">
              {activeMalwareAttacks.length > 0 ? '🔄' : discoveredEmails.length > 0 ? '🎯' : '🔒'}
            </span>
            <span className="stage-text">Stage 2: Malware Attack</span>
          </div>
          <div className="stage-arrow">→</div>
          <div className="stage-item" style={{ color: '#666' }}>
            <span className="stage-icon">🔒</span>
            <span className="stage-text">Stage 3: Vault Looting</span>
          </div>
        </div>

        {/* Casino Hack Cancel Button - Show for all active stages */}
        {user.casinoHack && user.casinoHack.isActive && user.casinoHack.currentStage === 2 && (
          <div className="casino-hack-cancel" style={{
            background: 'linear-gradient(135deg, #ff4444, #cc3333)',
            border: '2px solid #ff6666',
            borderRadius: '12px',
            padding: '20px',
            margin: '20px 0',
            textAlign: 'center'
          }}>
            <h4 style={{ color: '#fff', margin: '0 0 15px 0', fontSize: '18px' }}>
              🎰 Active Casino Hack: {user.casinoHack.casinoName}
            </h4>
            <div style={{ color: '#ffcccc', marginBottom: '15px', fontSize: '14px' }}>
              <p style={{ margin: '5px 0' }}>
                <strong>Current Stage:</strong> Stage 2 - Malware Attack
              </p>
              {user.casinoHack.currentStage === 3 && user.casinoHack.totalLooted > 0 && (
                <p style={{ margin: '5px 0' }}>
                  <strong>Total Looted:</strong> {user.casinoHack.totalLooted} WR Coins
                </p>
              )}
            </div>
            <button
              onClick={cancelCasinoHack}
              disabled={cancellingHack}
              style={{
                background: 'linear-gradient(45deg, #ff0000, #cc0000)',
                color: '#fff',
                border: 'none',
                padding: '12px 25px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: cancellingHack ? 'not-allowed' : 'pointer',
                opacity: cancellingHack ? 0.6 : 1,
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                if (!cancellingHack) {
                  e.target.style.background = 'linear-gradient(45deg, #ff3333, #ee0000)';
                }
              }}
              onMouseLeave={(e) => {
                if (!cancellingHack) {
                  e.target.style.background = 'linear-gradient(45deg, #ff0000, #cc0000)';
                }
              }}
            >
              {cancellingHack ? '🔄 Cancelling...' : '🚫 Cancel Casino Hack'}
            </button>
          </div>
        )}

        <div className="execution-form">
          <div className="input-group">
            <label htmlFor="emailAddress">Target Email Address (Discovered from Stage 1):</label>
            
            {/* Dropdown for discovered emails */}
            {discoveredEmails.length > 0 && (
              <div className="email-selection">
                <label>📋 Select from discovered emails:</label>
                <select
                  id="emailSelect"
                  value={emailAddress}
                  onChange={(e) => setEmailAddress(e.target.value)}
                  disabled={actionLoading}
                  className="email-select"
                >
                  <option value="">Select discovered employee email...</option>
                  {discoveredEmails.map((email, index) => (
                    <option key={index} value={email}>{email}</option>
                  ))}
                </select>
                
                {/* Email management buttons */}
                <div className="email-management">
                  <h4>📧 Manage Discovered Emails:</h4>
                  <div className="email-list">
                    {discoveredEmails.map((email, index) => (
                      <div key={index} className="email-item">
                        <span className="email-address">{email}</span>
                        <button
                          onClick={() => deleteDiscoveredEmail(email)}
                          disabled={actionLoading}
                          className="delete-email-button"
                          title="Delete this discovered email"
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            
            {/* Show message when no emails discovered */}
            {discoveredEmails.length === 0 && (
              <div className="no-emails-message" style={{
                background: 'linear-gradient(135deg, #2C3E50, #34495E)',
                border: '2px solid #3498DB',
                borderRadius: '12px',
                padding: '25px',
                margin: '20px 0',
                textAlign: 'center'
              }}>
                <div className="no-emails-content">
                  <span className="no-emails-icon" style={{ fontSize: '48px', display: 'block', marginBottom: '15px' }}>📧</span>
                  <h4 style={{ color: '#3498DB', margin: '0 0 15px 0', fontSize: '20px' }}>No Discovered Employee Emails</h4>
                  <p style={{ color: '#ECF0F1', margin: '10px 0', fontSize: '16px', lineHeight: '1.5' }}>
                    🎯 Complete <strong>Stage 1</strong> of a casino vault hack using <strong>Network Injection</strong> to discover employee email addresses.
                  </p>
                  <div style={{ 
                    background: 'rgba(52, 152, 219, 0.1)', 
                    border: '1px solid #3498DB', 
                    borderRadius: '8px', 
                    padding: '15px', 
                    margin: '15px 0' 
                  }}>
                    <p style={{ color: '#5DADE2', margin: '0', fontSize: '14px', fontWeight: 'bold' }}>
                      📍 Step-by-Step Guide:
                    </p>
                    <ol style={{ color: '#BDC3C7', textAlign: 'left', margin: '10px 0 0 20px', fontSize: '14px' }}>
                      <li>Go to <strong>Hack The Planet</strong> map</li>
                      <li>Find a <strong>Casino</strong> (🎰 icon)</li>
                      <li>Use <strong>Network Injection Tool</strong> for Stage 1</li>
                      <li>Wait 14-16 hours for Stage 1 completion</li>
                      <li>Return here - employee email will appear automatically!</li>
                    </ol>
                  </div>
                  <p style={{ color: '#F39C12', margin: '10px 0 0 0', fontSize: '13px', fontStyle: 'italic' }}>
                    💡 Tip: The malware tool refreshes every 10 seconds to check for new emails
                  </p>
                </div>
              </div>
            )}
          </div>
          
          {error && (
            <div className="error-message">
              ❌ {error}
            </div>
          )}

          <button
            onClick={executeMalware}
            disabled={actionLoading || !emailAddress.trim()}
            className="execute-button"
            style={{
              opacity: !emailAddress.trim() ? 0.5 : 1,
              cursor: !emailAddress.trim() ? 'not-allowed' : 'pointer'
            }}
          >
            {actionLoading ? '🔄 Executing...' : '🚀 Execute Malware'}
          </button>
        </div>

        <div className="malware-info">
          <h4>📋 Stage 2: Malware Attack Instructions</h4>
          <ul>
            <li>🎯 <strong>Step 1:</strong> Complete Stage 1 (Network Injection) to discover casino employee emails</li>
            <li>📧 <strong>Step 2:</strong> Select discovered employee email from the dropdown above</li>
            <li>⏱️ <strong>Duration:</strong> 12-16 hours (reduced by malware parts: -30min each)</li>
            <li>🚨 <strong>Risk:</strong> 20-30% chance of SWAT detection during attack</li>
            <li>✅ <strong>Success:</strong> Unlocks Stage 3 vault looting at 3 WR coins/second</li>
          </ul>
          {discoveredEmails.length > 0 && (
            <div className="discovered-emails-info">
              <h5>🔍 Your Discovered Employee Emails ({discoveredEmails.length}):</h5>
              <div className="emails-list">
                {discoveredEmails.map((email, index) => (
                  <div key={index} className="email-item">
                    <span className="email-icon">📧</span>
                    <span className="email-text">{email}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Stage 3 Vault Looting */}
      {user.casinoHack && user.casinoHack.isActive && user.casinoHack.currentStage >= 3 && (
        <div className="stage3-looting">
          <h3>💰 Stage 3: Vault Looting</h3>
          <div className="stage3-content">
            <div className="stage3-info">
              <h4>🎯 {user.casinoHack.casinoName} Vault Access</h4>
              <p><strong>💰 Earning Rate:</strong> 3 WR Coins per second</p>
              <p><strong>🏦 Total Looted:</strong> {user.casinoHack.totalLooted || 0} WR Coins</p>
              {user.casinoHack.lastCoinTime && (
                <p><strong>⏰ Last Collection:</strong> {new Date(user.casinoHack.lastCoinTime).toLocaleTimeString()}</p>
              )}
            </div>
            
            <div className="stage3-actions">
              {!user.casinoHack.lastCoinTime ? (
                <button
                  onClick={startStage3Looting}
                  disabled={stage3Loading}
                  className="stage3-start-button"
                  style={{
                    background: 'linear-gradient(45deg, #ff6b35, #ff8e53)',
                    color: 'white',
                    border: 'none',
                    padding: '15px 25px',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: stage3Loading ? 'not-allowed' : 'pointer',
                    opacity: stage3Loading ? 0.6 : 1,
                    width: '100%',
                    margin: '10px 0'
                  }}
                >
                  {stage3Loading ? '🔄 Starting...' : '🚀 Start Vault Looting'}
                </button>
              ) : (
                <button
                  onClick={collectStage3Coins}
                  disabled={stage3Loading}
                  className="stage3-collect-button"
                  style={{
                    background: 'linear-gradient(45deg, #4CAF50, #66BB6A)',
                    color: 'white',
                    border: 'none',
                    padding: '15px 25px',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: stage3Loading ? 'not-allowed' : 'pointer',
                    opacity: stage3Loading ? 0.6 : 1,
                    width: '100%',
                    margin: '10px 0'
                  }}
                >
                  {stage3Loading ? '🔄 Collecting...' : '💰 Collect Vault Coins'}
                </button>
              )}
              
              {/* Stage 3 Cancel Button */}
              <button
                onClick={cancelCasinoHack}
                disabled={cancellingHack || stage3Loading}
                style={{
                  background: 'linear-gradient(45deg, #ff4444, #cc3333)',
                  color: 'white',
                  border: 'none',
                  padding: '12px 20px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: (cancellingHack || stage3Loading) ? 'not-allowed' : 'pointer',
                  opacity: (cancellingHack || stage3Loading) ? 0.6 : 1,
                  width: '100%',
                  marginTop: '10px'
                }}
              >
                {cancellingHack ? '🔄 Cancelling...' : '🚫 Stop Vault Hack'}
              </button>
            </div>
            
            <div className="stage3-help">
              <h5>📋 Stage 3 Instructions:</h5>
              <ul>
                <li>🎯 <strong>Active Looting:</strong> You are earning 3 WR coins every second</li>
                <li>💰 <strong>Collect Regularly:</strong> Click "Collect Vault Coins" to add coins to your balance</li>
                <li>⏰ <strong>Continuous Earning:</strong> Coins accumulate even when you're not actively playing</li>
                <li>🚨 <strong>Risk:</strong> SWAT may arrive at any time - be ready to escape!</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Active Malware Attacks */}
      <div className="active-malware-attacks">
        <h3>🔥 Active Malware Operations</h3>
        
        {activeMalwareAttacks.length === 0 ? (
          <div className="no-attacks">
            <div className="no-attacks-content">
              <h4>💻 No Active Malware Attacks</h4>
              <p>You don't have any malware attacks running.</p>
              <p>Complete Stage 1 of a casino vault hack to get employee email addresses, then return here to execute malware attacks.</p>
            </div>
          </div>
        ) : (
          <div className="attacks-grid">
            {activeMalwareAttacks.map((attack) => {
              const isCompleted = attack.completed || new Date() >= new Date(attack.estimatedCompletion);
              const progress = getProgressPercentage(attack.startTime, attack.estimatedCompletion);
              const attackKey = `${attack.casinoId}-${attack.targetEmail}`;
              
              return (
                <div 
                  key={attackKey} 
                  className={`malware-attack-card ${isCompleted ? 'completed' : 'running'}`}
                >
                  <div className="attack-header">
                    <h4>📧 {attack.targetEmail}</h4>
                    <div className={`status-badge ${isCompleted ? 'completed' : 'running'}`}>
                      {isCompleted ? '✅ Ready' : '🔄 Infiltrating'}
                    </div>
                  </div>
                  
                  <div className="attack-progress">
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ width: `${progress}%` }}
                      ></div>
                    </div>
                    <div className="progress-text">
                      {Math.round(progress)}% Complete
                    </div>
                  </div>
                  
                  <div className="attack-details">
                    <div className="detail-row">
                      <span className="label">Started:</span>
                      <span className="value">
                        {new Date(attack.startTime).toLocaleDateString()} {new Date(attack.startTime).toLocaleTimeString()}
                      </span>
                    </div>
                    <div className="detail-row">
                      <span className="label">Time Remaining:</span>
                      <span className="value">
                        {formatTimeRemaining(attack.estimatedCompletion)}
                      </span>
                    </div>
                    <div className="detail-row">
                      <span className="label">Casino:</span>
                      <span className="value">{attack.casinoName || 'Unknown'}</span>
                    </div>
                  </div>
                  
                  {isCompleted ? (
                    <div className="completed-actions">
                      <button 
                        className="collect-button"
                        onClick={() => checkMalwareResults(`${attack.casinoId}-${attack.targetEmail}`)}
                        style={{
                          background: 'linear-gradient(45deg, #4CAF50, #66BB6A)',
                          color: 'white',
                          border: 'none',
                          padding: '12px 20px',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          width: '100%',
                          marginBottom: '8px'
                        }}
                      >
                        🎯 Check Results & Start Stage 3
                      </button>
                      <div style={{
                        background: 'rgba(76, 175, 80, 0.1)',
                        border: '1px solid #4CAF50',
                        borderRadius: '4px',
                        padding: '8px',
                        fontSize: '12px',
                        color: '#4CAF50',
                        textAlign: 'center'
                      }}>
                        ✅ Stage 2 Ready - Click to proceed to vault looting!
                      </div>
                    </div>
                  ) : (
                    <button 
                      className="stop-button"
                      onClick={() => stopMalwareAttack(`${attack.casinoId}-${attack.targetEmail}`)}
                    >
                      ❌ Stop Attack
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Stage 3 Vault Looting - Bottom Display */}
      {user.casinoHack && user.casinoHack.isActive && user.casinoHack.currentStage >= 3 && (
        <div className="stage3-looting" style={{
          background: 'linear-gradient(135deg, #ff6b35, #ff8e53)',
          border: '2px solid #ff8e53',
          borderRadius: '12px',
          padding: '25px',
          margin: '25px 0',
          color: 'white',
          boxShadow: '0 8px 16px rgba(255, 107, 53, 0.3)'
        }}>
          <div className="stage3-header" style={{ textAlign: 'center', marginBottom: '20px' }}>
            <h3 style={{ margin: '0', fontSize: '24px', color: '#fff' }}>
              💰 Stage 3: Vault Looting - {user.casinoHack.casinoName}
            </h3>
            <p style={{ margin: '10px 0 0 0', color: '#fff2e6', fontSize: '16px' }}>
              Active vault breach - collect coins but beware of SWAT!
            </p>
          </div>
          
          <div className="stage3-stats" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '15px',
            marginBottom: '20px'
          }}>
            <div className="stat-item" style={{
              background: 'rgba(255,255,255,0.1)',
              padding: '15px',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '20px', marginBottom: '8px' }}>💰</div>
              <div style={{ fontSize: '12px', color: '#fff2e6' }}>EARNING RATE</div>
              <div style={{ fontSize: '16px', fontWeight: 'bold' }}>3 WR/sec</div>
            </div>
            <div className="stat-item" style={{
              background: 'rgba(255,255,255,0.1)',
              padding: '15px',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '20px', marginBottom: '8px' }}>🏦</div>
              <div style={{ fontSize: '12px', color: '#fff2e6' }}>TOTAL LOOTED</div>
              <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{user.casinoHack.totalLooted || 0}</div>
            </div>
            {user.casinoHack.lastCoinTime && (
              <div className="stat-item" style={{
                background: 'rgba(255,255,255,0.1)',
                padding: '15px',
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '20px', marginBottom: '8px' }}>⏰</div>
                <div style={{ fontSize: '12px', color: '#fff2e6' }}>LAST COLLECTION</div>
                <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
                  {new Date(user.casinoHack.lastCoinTime).toLocaleTimeString()}
                </div>
              </div>
            )}
          </div>
          
          <div className="stage3-actions" style={{ textAlign: 'center' }}>
            {!user.casinoHack.lastCoinTime ? (
              <button
                onClick={startStage3Looting}
                disabled={stage3Loading}
                style={{
                  background: 'linear-gradient(45deg, #28a745, #20c997)',
                  color: 'white',
                  border: 'none',
                  padding: '15px 30px',
                  borderRadius: '8px',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  width: '100%',
                  maxWidth: '300px',
                  marginBottom: '15px',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                  transition: 'all 0.3s ease'
                }}
              >
                {stage3Loading ? '🔄 Starting...' : '🚀 Start Vault Looting'}
              </button>
            ) : (
              <button
                onClick={collectStage3Coins}
                disabled={stage3Loading}
                style={{
                  background: 'linear-gradient(45deg, #28a745, #20c997)',
                  color: 'white',
                  border: 'none',
                  padding: '15px 30px',
                  borderRadius: '8px',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  width: '100%',
                  maxWidth: '300px',
                  marginBottom: '15px',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                  transition: 'all 0.3s ease'
                }}
              >
                {stage3Loading ? '🔄 Collecting...' : '💰 Collect Looted Coins'}
              </button>
            )}
            
            <div className="stage3-warning" style={{
              background: 'rgba(0,0,0,0.2)',
              border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: '8px',
              padding: '12px',
              fontSize: '14px',
              color: '#fff2e6',
              textAlign: 'center',
              maxWidth: '400px',
              margin: '0 auto'
            }}>
              ⚠️ <strong>SWAT Risk:</strong> Random arrival time (30min-12hrs) - longer looting = higher risk!
            </div>
          </div>
        </div>
      )}

      {/* Equipment Info */}
      <div className="equipment-info">
        <h3>🛠️ Equipment Information</h3>
        <div className="equipment-details">
          <div className="equipment-stat">
            <span className="equipment-label">Malware Parts Owned:</span>
            <span className="equipment-value">{malwarePartsCount}</span>
          </div>
          <div className="equipment-stat">
            <span className="equipment-label">Time Reduction:</span>
            <span className="equipment-value">-{malwarePartsCount * 0.5} hours</span>
          </div>
          <div className="equipment-stat">
            <span className="equipment-label">SWAT Detection Risk:</span>
            <span className="equipment-value">20% chance</span>
          </div>
        </div>
        <p className="equipment-note">
          💡 Purchase malware parts in the <strong>Shop</strong> to reduce attack times. 
          Each part reduces the random attack time by 30 minutes.
        </p>
      </div>
    </div>
  );
};

export default MalwareTool;